import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, Clock, CheckCircle, Mail, ArrowUpDown, ArrowUp, ArrowDown, Phone, Building2, Globe, Heart, Info, Loader2, MessageSquare } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { directoryApi, profileApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import RestrictedOverlay from './ui/RestrictedOverlay';
import { getTierColor } from '../utils/tierColors';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { getLogoOrInitials } from '../utils/companyLogo';
import { SearchableSelect } from './ui/searchable-select';
import {
  getCityOptionsForCountry,
  getCountryIsoCode,
  useCanonicalCountryOptions,
} from '../utils/countries';

const DirectoryEnhanced = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { member } = useAuth();
  const { hasRestriction, getRestrictionMessage, hasFeatureAccess, loading: tierLoading } = useTier();
  const currentMemberId = member?.member_id;
  const isRegisteredUserTier = (tier) =>
    String(tier || '').trim().toLowerCase() === 'registered user';
  const isRegisteredUserViewer = isRegisteredUserTier(member?.tier);
  const shouldDisableContact = (directoryMember) =>
    isRegisteredUserViewer || isRegisteredUserTier(directoryMember?.tier);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTier, setSelectedTier] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedSpecialization, setSelectedSpecialization] = useState('all');
  const [sortField, setSortField] = useState('company_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [membersPerPage, setMembersPerPage] = useState(10);
  const [tierPriority, setTierPriority] = useState({
    Founder: 4999,
    Titanium: 3999,
    Rhodium: 2999,
    Platinum: 1999,
    Gold: 999,
    Silver: 0,
    'Registered User': -1,
    registered_user: -1,
    free_trial: -2,
  });

  const [ratingCriteria, setRatingCriteria] = useState(null);
  const [showRatingInfo, setShowRatingInfo] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberDetail, setShowMemberDetail] = useState(false);
  const [likedMemberIds, setLikedMemberIds] = useState(new Set());

  const tiers = ['all', 'Founder', 'Titanium', 'Rhodium', 'Platinum', 'Gold', 'Silver'];
  const LIKED_SERVICE_TYPE = 'liked';
  const specializations = ['all', 'Air Freight', 'Ocean Freight', 'Project Cargo', 'Customs Clearance', 'Warehousing', 'E-commerce Fulfilment', 'LCL', 'Breakbulk', '3PL Solutions'];

  const countryOptions = useCanonicalCountryOptions(true);

  const countryIsoCode = useMemo(() => {
    if (!selectedCountry || selectedCountry === 'all') return null;
    return getCountryIsoCode(selectedCountry);
  }, [selectedCountry]);

  const cityOptions = useMemo(() => {
    if (!countryIsoCode) return [{ value: 'all', label: 'All Cities' }];
    const cities = getCityOptionsForCountry(selectedCountry);
    return [{ value: 'all', label: 'All Cities' }, ...cities];
  }, [countryIsoCode, selectedCountry]);

  useEffect(() => {
    // Prevent directory API calls for users without feature access.
    if (tierLoading) return;
    if (!hasFeatureAccess('directory')) {
      setLoading(false);
      return;
    }
    fetchMembers();
    fetchRatingCriteria();
    fetchTierPricing();
  }, [hasFeatureAccess, tierLoading]);

  useEffect(() => {
    setSelectedCity('all');
  }, [selectedCountry]);

  useEffect(() => {
    const openMemberId = location.state?.openMemberId;
    if (!openMemberId || loading) return;
    const directoryMember = members.find((item) => item.member_id === openMemberId);
    if (directoryMember) {
      setSelectedMember(directoryMember);
      setShowMemberDetail(true);
    } else {
      directoryApi.getMember(openMemberId)
        .then((response) => {
          if (response.data) {
            setSelectedMember(response.data);
            setShowMemberDetail(true);
          }
        })
        .catch(() => toast.error('Company profile is not available.'));
    }
    navigate('/directory', { replace: true, state: null });
  }, [loading, location.state, members, navigate]);

  useEffect(() => {
    const fetchLikedMembers = async () => {
      if (!currentMemberId) return;
      // If directory is not accessible, no need to fetch liked members.
      if (tierLoading) return;
      if (!hasFeatureAccess('directory')) return;
      try {
        const res = await profileApi.getProfile(currentMemberId);
        const preferred = res.data?.preferred_partners || {};
        const liked = new Set(preferred[LIKED_SERVICE_TYPE] || []);
        setLikedMemberIds(liked);
      } catch (err) {
        console.error('Failed to fetch liked members:', err);
      }
    };
    fetchLikedMembers();
  }, [currentMemberId, hasFeatureAccess, tierLoading]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedTier !== 'all') params.tier = selectedTier;
      if (selectedCountry !== 'all') params.country = selectedCountry;
      if (selectedCity !== 'all') params.city = selectedCity;
      if (selectedSpecialization !== 'all') params.specialization = selectedSpecialization;

      const response = await directoryApi.getMembers(params);
      const visibleMembers = (response.data || []).filter(
        (directoryMember) => directoryMember?.member_id !== currentMemberId
      );
      setMembers(visibleMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const fetchRatingCriteria = async () => {
    try {
      const response = await directoryApi.getRatingCriteria();
      setRatingCriteria(response.data);
    } catch (error) {
      console.error('Error fetching rating criteria:', error);
    }
  };

  const fetchTierPricing = async () => {
    try {
      const response = await profileApi.getTiers();
      const liveTierPricing = (response.data || []).reduce((acc, tier) => {
        const annualPrice = Number(tier.price_annual ?? tier.price ?? 0);
        if (tier.display_name) acc[tier.display_name] = annualPrice;
        if (tier.name) acc[tier.name] = annualPrice;
        if (tier.tier_name) acc[tier.tier_name] = annualPrice;
        return acc;
      }, {
        'Registered User': -1,
        registered_user: -1,
        free_trial: -2,
      });

      setTierPriority((prev) => ({ ...prev, ...liveTierPricing }));
    } catch (error) {
      console.error('Error fetching tier pricing:', error);
    }
  };

  // Debounced search and refetch when currentMemberId changes (e.g. after login)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMembers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedTier, selectedCountry, selectedCity, selectedSpecialization, currentMemberId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedTier, selectedCountry, selectedCity, selectedSpecialization, sortField, sortDirection, viewMode, membersPerPage]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="text-gray-400" />;
    return sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  // Directory order is fixed by business priority:
  // highest tier fee, then highest rating, then latest created member.
  const sortedMembers = members.filter(
    (directoryMember) => directoryMember?.member_id !== currentMemberId
  ).sort((a, b) => {
    const aTierPriority = tierPriority[a.tier] ?? -99;
    const bTierPriority = tierPriority[b.tier] ?? -99;
    if (aTierPriority !== bTierPriority) {
      return bTierPriority - aTierPriority;
    }

    const aRating = Number(a.member_rating) || 0;
    const bRating = Number(b.member_rating) || 0;
    if (aRating !== bRating) {
      return bRating - aRating;
    }

    const aCreatedAt = new Date(a.created_at || 0).getTime();
    const bCreatedAt = new Date(b.created_at || 0).getTime();
    if (aCreatedAt !== bCreatedAt) {
      return bCreatedAt - aCreatedAt;
    }

    let aVal, bVal;
    switch (sortField) {
      case 'company_name':
        aVal = (a.company_name || '').toLowerCase();
        bVal = (b.company_name || '').toLowerCase();
        break;
      case 'country':
        aVal = (a.country || '').toLowerCase();
        bVal = (b.country || '').toLowerCase();
        break;
      case 'tier':
        aVal = tierPriority[a.tier] ?? -99;
        bVal = tierPriority[b.tier] ?? -99;
        break;
      case 'rating':
        aVal = a.member_rating || 0;
        bVal = b.member_rating || 0;
        break;
      case 'enquiries':
        aVal = a.enquiries_sent || 0;
        bVal = b.enquiries_sent || 0;
        break;
      case 'created_at':
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedMembers.length / membersPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedMembers = sortedMembers.slice(
    (safeCurrentPage - 1) * membersPerPage,
    safeCurrentPage * membersPerPage
  );
  const paginationStart = sortedMembers.length === 0 ? 0 : (safeCurrentPage - 1) * membersPerPage + 1;
  const paginationEnd = Math.min(safeCurrentPage * membersPerPage, sortedMembers.length);

  const handleContactMember = (member) => {
    if (shouldDisableContact(member)) return;
    setSelectedMember(member);
    setShowMemberDetail(true);
  };

  const handleSendMessage = (member) => {
    if (shouldDisableContact(member)) return;
    // Navigate to chat with member info in state
    navigate('/chat', {
      state: {
        openChatWith: {
          member_id: member.member_id,
          company_name: member.company_name,
          individual_name: member.individual_name,
          country: member.country,
          city: member.city,
          tier: member.tier,
          member_rating: member.member_rating,
          company_logo: member.company_logo
        }
      }
    });
    toast.success(`Opening chat with ${member.company_name}`);
  };

  const handleToggleLike = async (member) => {
    if (!currentMemberId) {
      toast.error('Please login to like members');
      return;
    }
    const isLiked = likedMemberIds.has(member.member_id);
    try {
      if (isLiked) {
        await directoryApi.removePreferredPartner(currentMemberId, member.member_id, LIKED_SERVICE_TYPE);
        setLikedMemberIds((prev) => {
          const next = new Set(prev);
          next.delete(member.member_id);
          return next;
        });
        toast.success(`Removed ${member.company_name} from liked members`);
      } else {
        await directoryApi.setPreferredPartner(currentMemberId, member.member_id, LIKED_SERVICE_TYPE);
        setLikedMemberIds((prev) => new Set([...prev, member.member_id]));
        toast.success(`${member.company_name} added to liked members`);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update'));
    }
  };

  const renderPagination = () => {
    return (
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white/90 px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <p className="text-sm text-slate-600">
            Showing {paginationStart}-{paginationEnd} of {sortedMembers.length} members
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Show</span>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={membersPerPage}
              onChange={(e) => setMembersPerPage(Number(e.target.value))}
            >
              {[10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={safeCurrentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          >
            Previous
          </Button>
          <span className="min-w-24 text-center text-sm font-medium text-slate-700">
            Page {safeCurrentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={safeCurrentPage === totalPages}
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const renderMemberCards = (className) => (
    <div className={className}>
      {paginatedMembers.map((member) => {
        const tierColor = getTierColor(member.tier);
        return (
          <Card key={member.member_id} className={`hover:shadow-lg transition-all duration-300 border-2 ${tierColor.border}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <img
                  src={getLogoOrInitials(member, member.company_name)}
                  alt={member.company_name}
                  className="w-12 h-12 rounded-lg object-cover"
                  onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(member.company_name)}`; }}
                />
                <div className="flex items-center space-x-1">
                  {member.verified && <CheckCircle size={16} className="text-blue-600" />}
                  {member.ho_type && (
                    <Badge variant="outline" className="text-xs">
                      {member.ho_type === 'Global HO' ? <Globe size={10} /> : <Building2 size={10} />}
                    </Badge>
                  )}
                </div>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm mb-1 break-words md:truncate">{member.company_name}</h3>
              <p className="text-xs text-gray-500 mb-1 break-words">{member.individual_name}</p>
              <div className="flex items-center text-xs text-gray-600 mb-3 min-w-0">
                <MapPin size={12} className="mr-1 shrink-0" />
                <span className="break-words">{member.city}, {member.country}</span>
              </div>
              <Badge className={`${tierColor.badge} mb-3`}>{member.tier}</Badge>
              <div className="flex flex-wrap gap-1 mb-3">
                {(member.specializations || []).slice(0, 2).map((spec, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {spec}
                  </Badge>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs mb-3 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center">
                  <Star size={12} className="text-amber-500 mr-1" fill="#F59E0B" />
                  <span className="font-medium">{member.member_rating?.toFixed(1) || 'N/A'}</span>
                </div>
                <span className="min-w-0 break-words">{member.enquiries_sent || 0} enquiries</span>
                <div className="col-span-2 flex items-center min-w-0 sm:col-span-1">
                  <Clock size={12} className="mr-1 shrink-0" />
                  <span className="break-words">{member.response_time || 'N/A'}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300"
                  onClick={() => handleContactMember(member)}
                  disabled={shouldDisableContact(member)}
                >
                  <Mail size={14} className="mr-1" />
                  Contact
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className={`h-8 w-8 p-0 shrink-0 ${likedMemberIds.has(member.member_id) ? 'text-red-500 border-red-200' : ''}`}
                        onClick={() => handleToggleLike(member)}
                      >
                        <Heart size={14} className={likedMemberIds.has(member.member_id) ? 'fill-red-500' : ''} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{likedMemberIds.has(member.member_id) ? 'Remove from liked' : 'Like this member'}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  if (tierLoading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        Loading directory...
      </div>
    );
  }

  if (!hasFeatureAccess('directory') || hasRestriction('directory')) {
    return <RestrictedOverlay message={getRestrictionMessage('directory')} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Member Directory</h1>
          <p className="text-gray-600 mt-1">Connect with verified logistics partners worldwide</p>
        </div>
        <Button variant="outline" onClick={() => setShowRatingInfo(true)} data-testid="rating-info-btn">
          <Info size={16} className="mr-2" />
          Rating Criteria
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search by company name, member name, country, or city..."
                className="pl-10 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="directory-search"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Membership Tier</label>
                <select
                  className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-gray-800"
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value)}
                  data-testid="tier-filter"
                >
                  {tiers.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier === 'all' ? 'All Tiers' : tier}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Country</label>
                <SearchableSelect
                  options={countryOptions}
                  value={selectedCountry}
                  onChange={(value) => setSelectedCountry(value || 'all')}
                  placeholder="Search or select country"
                  data-testid="country-filter"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">City</label>
                <SearchableSelect
                  key={`city-${selectedCountry}`}
                  options={cityOptions}
                  value={selectedCity}
                  onChange={(value) => setSelectedCity(value || 'all')}
                  placeholder={countryIsoCode ? 'Search or select city' : 'Select country first'}
                  disabled={selectedCountry === 'all'}
                  data-testid="city-filter"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Specialization</label>
                <select
                  className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-gray-800"
                  value={selectedSpecialization}
                  onChange={(e) => setSelectedSpecialization(e.target.value)}
                  data-testid="specialization-filter"
                >
                  {specializations.map((spec) => (
                    <option key={spec} value={spec}>
                      {spec === 'all' ? 'All Specializations' : spec}
                    </option>
                  ))}
                </select>
              </div>

              <div className="hidden md:flex items-end gap-2">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'outline'}
                  onClick={() => setViewMode('table')}
                  size="sm"
                >
                  Table
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  onClick={() => setViewMode('grid')}
                  size="sm"
                >
                  Grid
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading members...</span>
        </div>
      ) : viewMode === 'table' ? (
        /* Mobile cards, desktop table */
        <>
          <div className="md:hidden">
            {renderMemberCards('grid grid-cols-1 gap-3')}
          </div>
          <Card className="hidden md:block">
          <CardContent className="p-0 overflow-hidden">
            <table className="w-full table-fixed text-xs">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-2 py-2 text-left w-[22%]">
                    <button
                      onClick={() => handleSort('company_name')}
                      className="flex items-center space-x-1 text-[10px] font-medium text-gray-700 uppercase hover:text-gray-900"
                    >
                      <span>Member</span>
                      {getSortIcon('company_name')}
                    </button>
                  </th>
                  <th className="px-2 py-2 text-left w-[10%]">
                    <button
                      onClick={() => handleSort('country')}
                      className="flex items-center space-x-1 text-[10px] font-medium text-gray-700 uppercase hover:text-gray-900"
                    >
                      <span>Location</span>
                      {getSortIcon('country')}
                    </button>
                  </th>
                  <th className="px-2 py-2 text-left w-[8%]">
                    <button
                      onClick={() => handleSort('tier')}
                      className="flex items-center space-x-1 text-[10px] font-medium text-gray-700 uppercase hover:text-gray-900"
                    >
                      <span>Tier</span>
                      {getSortIcon('tier')}
                    </button>
                  </th>
                  <th className="px-2 py-2 text-left w-[18%]">
                    <span className="text-[10px] font-medium text-gray-700 uppercase">Specializations</span>
                  </th>
                  <th className="px-3 py-2 text-left w-[9%]">
                    <button
                      onClick={() => handleSort('rating')}
                      className="flex items-center space-x-1 text-[10px] font-medium text-gray-700 uppercase hover:text-gray-900"
                    >
                      <span>Rating</span>
                      {getSortIcon('rating')}
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left w-[9%]">
                    <button
                      onClick={() => handleSort('enquiries')}
                      className="flex items-center space-x-1 text-[10px] font-medium text-gray-700 uppercase hover:text-gray-900"
                    >
                      <span>Enquiries</span>
                      {getSortIcon('enquiries')}
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left w-[10%]">
                    <span className="text-[10px] font-medium text-gray-700 uppercase">Response</span>
                  </th>
                  <th className="px-2 py-2 text-left w-[14%] pr-2">
                    <span className="text-[10px] font-medium text-gray-700 uppercase">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedMembers.map((member) => {
                  const tierColor = getTierColor(member.tier);
                  return (
                    <tr key={member.member_id} className="hover:bg-gray-50 transition-colors" data-testid={`member-row-${member.member_id}`}>
                      <td className="px-2 py-2 min-w-0">
                        <div className="flex items-center space-x-2 min-w-0">
                          <img
                            src={getLogoOrInitials(member, member.company_name)}
                            alt={member.company_name}
                            className="w-8 h-8 rounded-lg shrink-0 object-cover"
                            onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(member.company_name)}`; }}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center space-x-1 min-w-0">
                              <p className="font-medium text-gray-900 break-words">{member.company_name}</p>
                              {member.verified && (
                                <CheckCircle size={12} className="text-blue-600 shrink-0" />
                              )}
                            </div>
                            <p className="text-[10px] text-gray-500 truncate">{member.individual_name}</p>
                            <p className="text-[10px] text-gray-400">ID: {member.member_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 min-w-0">
                        <div className="flex items-start text-gray-600 min-w-0">
                          <MapPin size={12} className="mr-1 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="block text-sm">{member.city || '-'}</span>
                            <span className="block text-[10px] text-gray-500">{member.country || '-'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <Badge className={`${tierColor.badge} text-[10px]`}>{member.tier}</Badge>
                      </td>
                      <td className="px-2 py-2 min-w-0">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex flex-wrap gap-0.5 min-w-0 overflow-hidden cursor-help">
                                {(member.specializations || []).slice(0, 2).map((spec, index) => (
                                  <Badge key={index} variant="outline" className="text-[10px]">
                                    {spec}
                                  </Badge>
                                ))}
                                {(member.specializations || []).length > 2 && (
                                  <Badge variant="outline" className="text-[10px]">
                                    +{member.specializations.length - 2}
                                  </Badge>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap text-left">
                              {(member.specializations || []).length > 0
                                ? member.specializations.join(', ')
                                : 'No specializations listed'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center">
                          <Star size={12} className="text-amber-500 mr-0.5" fill="#F59E0B" />
                          <span className="font-medium">{member.member_rating?.toFixed(1) || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-medium">{member.enquiries_sent || 0}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center text-gray-600">
                          <Clock size={12} className="mr-0.5" />
                          {member.response_time || 'N/A'}
                        </div>
                      </td>
                      <td className="px-2 py-2 pr-2 overflow-visible">
                        <div className="flex items-center gap-1 flex-nowrap shrink-0">
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] h-7 px-2 shrink-0 disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300"
                            onClick={() => handleContactMember(member)}
                            disabled={shouldDisableContact(member)}
                            data-testid={`contact-btn-${member.member_id}`}
                          >
                            <Mail size={12} className="mr-0.5" />
                            Contact
                          </Button>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={`h-7 w-7 p-0 shrink-0 ${likedMemberIds.has(member.member_id) ? 'text-red-500 border-red-200' : ''}`}
                                  onClick={() => handleToggleLike(member)}
                                >
                                  <Heart size={12} className={likedMemberIds.has(member.member_id) ? 'fill-red-500' : ''} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{likedMemberIds.has(member.member_id) ? 'Remove from liked' : 'Like this member'}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
          </Card>
        </>
      ) : (
        /* Grid View */
        renderMemberCards('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4')
      )}

      {renderPagination()}

      {/* Member Detail Dialog */}
      <Dialog open={showMemberDetail} onOpenChange={setShowMemberDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <img
                  src={getLogoOrInitials(selectedMember, selectedMember.company_name)}
                  alt={selectedMember.company_name}
                  className="w-16 h-16 rounded-lg object-cover"
                  onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(selectedMember.company_name)}`; }}
                />
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{selectedMember.company_name}</h3>
                  <p className="text-gray-600">{selectedMember.individual_name} - {selectedMember.role}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Badge className={getTierColor(selectedMember.tier).badge}>{selectedMember.tier}</Badge>
                    {selectedMember.ho_type && (
                      <Badge variant="outline">{selectedMember.ho_type}</Badge>
                    )}
                    {selectedMember.verified && (
                      <Badge className="bg-blue-100 text-blue-700">Verified</Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p className="flex items-center text-gray-600">
                    <MapPin size={16} className="mr-2" />
                    {selectedMember.city}, {selectedMember.country}
                  </p>
                  <p className="flex items-center text-gray-600">
                    <Mail size={16} className="mr-2" />
                    {selectedMember.email}
                  </p>
                  <p className="flex items-center text-gray-600">
                    <Phone size={16} className="mr-2" />
                    {selectedMember.phone}
                  </p>
                </div>
                <div className="space-y-2">
                  <p><strong>Member ID:</strong> {selectedMember.member_id}</p>
                  <p><strong>Rating:</strong> {selectedMember.member_rating?.toFixed(1)} / 5.0</p>
                  <p><strong>Enquiries Sent:</strong> {selectedMember.enquiries_sent || 0}</p>
                  <p><strong>Response Time:</strong> {selectedMember.response_time}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Specializations</h4>
                <div className="flex flex-wrap gap-2">
                  {(selectedMember.specializations || []).map((spec, i) => (
                    <Badge key={i} variant="outline">{spec}</Badge>
                  ))}
                </div>
              </div>

              {selectedMember.linkedin && (
                <div>
                  <h4 className="font-medium mb-2">LinkedIn</h4>
                  <a href={`https://${selectedMember.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {selectedMember.linkedin}
                  </a>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowMemberDetail(false)}>Close</Button>
                <Button
                  variant="outline"
                  className={likedMemberIds.has(selectedMember.member_id) ? 'text-red-500 border-red-200' : ''}
                  onClick={() => handleToggleLike(selectedMember)}
                >
                  <Heart size={16} className={`mr-2 ${likedMemberIds.has(selectedMember.member_id) ? 'fill-red-500' : ''}`} />
                  {likedMemberIds.has(selectedMember.member_id) ? 'Unlike' : 'Like'}
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300"
                  disabled={shouldDisableContact(selectedMember)}
                  onClick={() => {
                    setShowMemberDetail(false);
                    handleSendMessage(selectedMember);
                  }}
                >
                  <MessageSquare size={16} className="mr-2" />
                  Send Message
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rating Criteria Dialog */}
      <Dialog open={showRatingInfo} onOpenChange={setShowRatingInfo}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>Member Rating Criteria</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-6">
            {ratingCriteria?.criteria?.length ? (
              <div className="space-y-4 pb-4">
                <p className="text-sm text-gray-600">
                  Member ratings are calculated based on the following weighted criteria:
                </p>
                <div className="space-y-3">
                  {ratingCriteria.criteria.map((criterion, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium">{criterion.name}</h4>
                        <Badge>{criterion.weight}%</Badge>
                      </div>
                      <p className="text-sm text-gray-600">{criterion.description}</p>
                      {criterion.levels && (
                        <div className="mt-2 text-xs text-gray-500">
                          Excellent: {criterion.levels.excellent} | Good: {criterion.levels.good} | Fair: {criterion.levels.fair}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Final Rating:</strong> All criteria are weighted and combined to produce a rating out of 5.0.
                    Ratings are updated in real-time as members complete transactions and interact on the platform.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 pb-4">Loading rating criteria...</p>
            )}
          </div>
          <div className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setShowRatingInfo(false)} className="w-full sm:w-auto">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DirectoryEnhanced;
